import { nanoid } from 'nanoid';

import i18n from '@sola/locales/index.js';
import { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { LocalAlertConfig, LocalAlertCreateProps } from '../types/localeAlerts';

/**
 * Function for creating local alert data with an id.
 */
export const createLocalAlert = (
  alertProps: LocalAlertCreateProps,
): LocalAlertConfig => {
  return { id: nanoid(), ...alertProps };
};

export const createPaymentMethodSuccessAlert = (
  { creditCard, ach }: GraphQLSupporterPaymentMethod,
  actionTranslationKey: string,
): LocalAlertCreateProps => {
  const { t } = i18n;
  let lastFour = creditCard?.lastFour || '';
  let label = creditCard?.cardType || '';

  if (ach) {
    lastFour = ach.lastFourOfAccountNumber || '';
    label = t('my:payments.methodTypes.ach.short');
  }

  return {
    translationKey: 'payments.success',
    variant: 'positive',
    translationData: {
      lastFour,
      label,
      action: t(`my:${actionTranslationKey}`),
    },
  };
};

export const createPaymentMethodPartialSuccessAlert =
  (): LocalAlertCreateProps => {
    const { t } = i18n;
    return {
      translationKey: 'payments.partialSuccess',
      variant: 'warning',
      dismissible: false,
      action: (
        <a href={t('my:ctas.contactSupport.url')}>
          {t('my:ctas.contactSupport.text')}
        </a>
      ),
    };
  };

export const removePaymentMethodPartialSuccessAlert =
  (): LocalAlertCreateProps => {
    const { t } = i18n;

    return {
      translationKey: 'payments.remove.partialSuccess',
      variant: 'negative',
      dismissible: true,
      action: i18n.exists('my:ctas.contactSupport.text') ? (
        <a href={t('my:ctas.contactSupport.url')}>
          {t('my:ctas.contactSupport.text')}
        </a>
      ) : undefined,
    };
  };

export const removePaymentMethodFailureAlert = (): LocalAlertCreateProps => {
  const { t } = i18n;

  return {
    translationKey: 'payments.remove.failure',
    variant: 'negative',
    dismissible: true,
    action: i18n.exists('my:ctas.contactSupport.text') ? (
      <a href={t('my:ctas.contactSupport.url')}>
        {t('my:ctas.contactSupport.text')}
      </a>
    ) : undefined,
  };
};

export const createPaymentPlanSuccessAlert = (): LocalAlertCreateProps => {
  return {
    translationKey: 'commitments.detailsSheet.paymentPlanSuccess',
    variant: 'positive',
    closable: true,
  };
};

export const createSupportAlert = (translationKey: string) => {
  const { t } = i18n;

  return createLocalAlert({
    translationKey,
    variant: 'negative',
    dismissible: true,
    action: i18n.exists('my:ctas.contactSupport.text') ? (
      <a href={t('my:ctas.contactSupport.url')}>
        {t('my:ctas.contactSupport.text')}
      </a>
    ) : undefined,
  });
};
